import React, { useEffect } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { additionalBody } from "../../components/layout/TwoColumn/styles.module.scss"

import Card from "../../components/layout/card"
import TwoColumn from "../../components/layout/TwoColumn/TwoColumn"
import PageMeta from "../../components/layout/PageMeta"
import CSNavbar from "../../components/navigation/Navbar/Navbar"
import Breadcrumbs from "../../components/navigation/breadcrumbs/Breadcrumbs"
import PageHeader from "../../components/PageHeader/PageHeader"
import ProviderListHeading from "../../components/provider/geo/provider_list_heading"
import ProviderList from "../../components/provider/provider_list/provider_list/ProviderList"
import MoyoSearchLink from "../../components/provider/geo/geo_browse_cta_link"
import GeoConnectiveLinks from "../../components/provider/geo/geo_connective_links"
import { renderIf } from "../../components/FeatureToggle/FeatureToggle"
import Articles from "../../components/provider/geo/articles"
import GeoFAQs from "../../components/provider/geo/FAQ/FAQ"
import HighestRatedTowns from "../../components/provider/HighestRatedTowns/HighestRatedTowns"
import AlternativeCareTypeCard from "../../components/provider/geo/AlternativeCareTypeCard/AlternativeCareTypeCard"
import Footer from "../../components/navigation/footer"

import { breadcrumbLinksForGeoLocation } from "../../utils/breadcrumbs"
import { geoAreaLocationProviderDirectoryURL } from "../../utils/paths"
import { POSTAL_TOWN_SCALE } from "../../utils/geographical_scales"

import {
  buildPageLoadEvent,
  filterLinks,
  metaDescription,
  metaTitle,
} from "../GeoTemplateHelpers"
import SingleGeoContext, {
  getSingleGeoContextData,
} from "../../contexts/single_geo"
import CareTypeContext, {
  getCareTypeContextData,
} from "../../contexts/care_type"
import { branchRecordType } from "../../shared-prop-types"
import Mixpanel from "../../components/layout/Mixpanel"

const careType = "home_care"

const GeoHomeCarePage = ({
  pageContext: {
    branchRecords,
    branchRecordCounts,
    totalBranchRecordCount,
    locationName,
    geoSelfPathData,
    geoChildrenPathData,
    geoParentsPathData,
    geoSiblingsPathData,
  },
}) => {
  useEffect(
    buildPageLoadEvent(
      careType,
      locationName,
      geoSelfPathData,
      branchRecordCounts,
      branchRecords.length,
    ),
    [],
  )

  const { regulatoryBody } = branchRecords[0]
  const geoSelf = getSingleGeoContextData(geoSelfPathData, {
    parentGeoPathData:
      geoParentsPathData.length > 0 ? geoParentsPathData[0] : null,
    siblingGeoPathDatas: geoSiblingsPathData,
    totalBranchRecordCount,
    regulatoryBody,
  })
  const isTownPage = geoSelfPathData.geographicScale === POSTAL_TOWN_SCALE
  const showTownLabel = !isTownPage
  const RenderIfNotTownAndHasFeaturedTowns = renderIf(
    !isTownPage && geoSelf.featuredHomeCareTowns.length > 0,
  )
  const currentPath = geoAreaLocationProviderDirectoryURL(geoSelfPathData)
  const breadcrumbLinks = breadcrumbLinksForGeoLocation(
    geoSelfPathData,
    branchRecords[0],
  )

  const pageTitle = metaTitle(careType, locationName)
  const pageDescription = metaDescription(
    careType,
    locationName,
    isTownPage,
    geoSelfPathData.homeCareCount,
  )
  const pageType = "Filtered Directory"

  const renderHeader = (
    <>
      <Mixpanel />
      <PageMeta
        currentUrl={currentPath}
        description={pageDescription}
        title={pageTitle}
      />
      <CSNavbar />
      <PageHeader
        locationName={locationName}
        pageType={pageType}
        currentUrl={currentPath}
        careType={careType}
      />
    </>
  )

  const renderTitle = (
    <ProviderListHeading
      locationName={locationName}
      pageType={careType}
      careTypeCount={totalBranchRecordCount}
    />
  )

  const renderMainBody = (
    <>
      <ProviderList
        branchRecords={branchRecords}
        showTownLabel={showTownLabel}
        locationName={locationName}
      />
      <MoyoSearchLink
        geoSelfPathData={geoSelfPathData}
        isTownPage={isTownPage}
      />
    </>
  )

  const renderAdditionalBody = (
    <div className={classNames(additionalBody)}>
      <GeoConnectiveLinks
        geosDown={filterLinks(careType, geoChildrenPathData)}
        geosUp={geoParentsPathData}
        geosSideways={filterLinks(careType, geoSiblingsPathData)}
        careType={careType}
        districtType={geoSelf.districtType}
      />
      <Card>
        <Articles regulatoryBody={regulatoryBody} pageType={careType} />
      </Card>
      <GeoFAQs />
      <Card noMarginAbove>
        <Breadcrumbs currentPath={currentPath} links={breadcrumbLinks} />
      </Card>
    </div>
  )

  const renderSidebar = (
    <>
      <RenderIfNotTownAndHasFeaturedTowns>
        <HighestRatedTowns
          locationName={locationName}
          careType={careType}
          regulatoryBody={regulatoryBody}
          featuredTowns={geoSelf.featuredHomeCareTowns}
        />
      </RenderIfNotTownAndHasFeaturedTowns>
      <AlternativeCareTypeCard careType={careType} />
    </>
  )

  const renderFooter = <Footer />

  return (
    <SingleGeoContext.Provider value={geoSelf}>
      <CareTypeContext.Provider value={getCareTypeContextData(careType)}>
        <TwoColumn
          header={renderHeader}
          title={renderTitle}
          mainBody={renderMainBody}
          additionalBody={renderAdditionalBody}
          sidebar={renderSidebar}
          footer={renderFooter}
          sidebarPositionOnMobile="centerOfColumn"
        />
      </CareTypeContext.Provider>
    </SingleGeoContext.Provider>
  )
}

const hasGeographicScaleShape = PropTypes.shape({
  geographicScale: PropTypes.string.isRequired,
  homeCareCount: PropTypes.number.isRequired,
})

GeoHomeCarePage.propTypes = {
  pageContext: PropTypes.shape({
    branchRecords: PropTypes.arrayOf(branchRecordType),
    branchRecordCounts: PropTypes.shape({
      countOfProvidersWithPhotos: PropTypes.number.isRequired,
      countOfProvidersWithIntroBlurb: PropTypes.number.isRequired,
      countOfProvidersWithPrices: PropTypes.number.isRequired,
      countOfPremiumProviders: PropTypes.number.isRequired,
    }),
    totalBranchRecordCount: PropTypes.number.isRequired,
    locationName: PropTypes.string.isRequired,
    geoSelfPathData: hasGeographicScaleShape,
    geoChildrenPathData: PropTypes.arrayOf(branchRecordType),
    geoParentsPathData: PropTypes.arrayOf(hasGeographicScaleShape),
    geoSiblingsPathData: PropTypes.arrayOf(hasGeographicScaleShape),
    featuredHomeCareTowns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        ratedPositiveCount: PropTypes.number,
      }),
    ),
  }).isRequired,
}

export default GeoHomeCarePage
